import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import VideoPlayer from './VideoPlayer';

type LazyVideoPlayerProps = {
  sourceUrl: string;
  rootMargin?: string;
};

/**
 * Lazy-loading wrapper for VideoPlayer using Intersection Observer.
 * Only renders the actual video player when the component is about to enter the viewport.
 * This prevents loading 30+ videos simultaneously and improves initial page load performance.
 */
export default function LazyVideoPlayer({
  sourceUrl,
  rootMargin = '200px', // Load videos 200px before they enter viewport
}: LazyVideoPlayerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        rootMargin,
        threshold: 0,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [rootMargin]);

  return (
    <Box
      ref={containerRef}
      data-testid="video-placeholder"
      sx={{
        width: '100%',
        height: '312px',
        background: '#d3d3d3',
        borderRadius: '5px',
        position: 'relative',
        display: 'inline-block',
        overflow: 'hidden',
      }}
    >
      {isVisible && <VideoPlayer sourceUrl={sourceUrl} />}
    </Box>
  );
}
